import { useEffect, useMemo, useState } from 'react';
import { endOfDay, format, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { hu } from 'date-fns/locale';
import { PiClockCounterClockwiseDuotone } from 'react-icons/pi';

import { Tooltip } from '@material-ui/core';

import OptimizationPipelinePopup, { OptimizationPipelinePopupHeader, PopupDateRangeQuickSelector, PopupToolbar, PopupToolbarGroup, PopupToolbarChip, PopupExportButtons } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';
import OptimizationPipelinePopupTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopupTable';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getSoftStatusClass } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/colorUtils';
import { parseTsToDate } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';
import { buildExportFilename, exportPopupTable } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';

const INITIAL_DATE_RANGE = { from: '', to: '', quickRange: 'ALL' };
const INITIAL_SORT = { key: 'ts', direction: 'desc' };
const STATUS_BADGE_CLASS = 'px-8 py-5 rounded-lg font-semibold whitespace-nowrap';
const STATUS_CELL_CLASS = 'flex flex-col text-12 px-8 py-5 rounded-lg';

function safeDate(value) { return parseTsToDate(value); }

function formatInputDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) { return ''; }
    return format(date, 'yyyy-MM-dd');
}

function formatDisplayDate(value) {
    const date = safeDate(value);
    return date ? format(date, 'yyyy.MM.dd.', { locale: hu }) : '';
}

function getDateRangeSubtitle(dateRange) {
    const from = formatDisplayDate(dateRange?.from);
    const to = formatDisplayDate(dateRange?.to);

    if (from && to) { return 'Showing data from ' + from + ' to ' + to; }
    if (from) { return 'Showing data from ' + from; }
    if (to) { return 'Showing data until ' + to; }

    return null;
}

function getStatusClass(color, baseClassName) {
    return baseClassName + ' ' + getSoftStatusClass(color);
}

function collectOutlierData(list) {
    const out = [];

    for (const entry of (Array.isArray(list) ? list : [])) {
        const ts = entry?.ts;
        const od = entry?.meta?.outlier_detection;
        const row = { ts, outlier: od?.outlier, detectors: {} };

        const details = Array.isArray(od?.details) ? od.details : [];

        for (const detail of details) {
            const name = detail?.outlier_detector_name || 'unknown';

            if (!row.detectors[name]) {
                row.detectors[name] = {
                    name: name,
                    version: detail?.outlier_detector_version,
                    prediction: detail?.prediction
                };
            }
        }

        out.push(row);
    }

    return out;
}

function getAllDetectorNames(data) {
    const set = new Set();

    for (const row of (Array.isArray(data) ? data : [])) {
        const keys = row?.detectors ? Object.keys(row.detectors) : [];
        keys.forEach((key) => set.add(key));
    }

    return Array.from(set);
}

function formatDate(value) {
    const date = safeDate(value);
    return date ? format(date, 'yyyy.MM.dd', { locale: hu }) : '-';
}

function formatOutlierValue(value) {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'Missing';
}

function formatPredictionValue(value) {
    if (value === true) return 'Prediction: Yes';
    if (value === false) return 'Prediction: No';
    return 'Prediction: -';
}

function renderOutlierBadge(value) {
    if (value === true) { return (<div className={getStatusClass('red', STATUS_BADGE_CLASS)}>Yes</div>); }
    if (value === false) { return (<div className={getStatusClass('green', STATUS_BADGE_CLASS)}>No</div>); }
    return (<div className={getStatusClass('gray', STATUS_BADGE_CLASS)}>Missing</div>);
}

function renderDetectorCell(row, name) {
    const detector = row?.detectors?.[name];
    const prediction = detector?.prediction;

    if (prediction === true) {
        return (
            <div className={getStatusClass('red', STATUS_CELL_CLASS)}>
                <span className='font-semibold'>Prediction: Yes</span>
            </div>
        );
    }

    if (prediction === false) {
        return (
            <div className={getStatusClass('green', STATUS_CELL_CLASS)}>
                <span className='font-semibold'>Prediction: No</span>
            </div>
        );
    }

    return (
        <div className={getStatusClass('gray', STATUS_CELL_CLASS)}>
            <span className='font-semibold'>Prediction: -</span>
        </div>
    );
}

export default function DataValidationHistoryPopup(props) {
    const { popup, closePopup, getOnDemandResource, ensureOnDemandResource, ts } = useOptimizationPipeline();

    const [dateRange, setDateRange] = useState(INITIAL_DATE_RANGE);
    const [outlierView, setOutlierView] = useState('has');
    const [tableSort, setTableSort] = useState(INITIAL_SORT);

    const mergedMeasurementResource = getOnDemandResource('mergedMeasurementDataList');
    const mergedMeasurementDataList = mergedMeasurementResource?.data;
    const mergedMeasurementDataListLoading = mergedMeasurementResource?.loading;
    const mergedMeasurementDataListError = mergedMeasurementResource?.error;

    useEffect(() => {
        if (!popup || popup.type !== 'DATA_VALIDATION_HISTORY') return;
        ensureOnDemandResource('mergedMeasurementDataList');
    }, [popup, ensureOnDemandResource]);

    useEffect(() => {
        if (!popup || popup.type !== 'DATA_VALIDATION_HISTORY') return;
        setTableSort(INITIAL_SORT);
        setOutlierView('has');
    }, [popup?.type, popup?.requestToken]);

    const outlierData = useMemo(() => {
        return collectOutlierData(mergedMeasurementDataList);
    }, [mergedMeasurementDataList]);

    const detectorNames = useMemo(() => {
        return getAllDetectorNames(outlierData);
    }, [outlierData]);

    const dateBounds = useMemo(() => {
        const dates = outlierData
            .map((row) => safeDate(row?.ts))
            .filter(Boolean)
            .sort((a, b) => a.getTime() - b.getTime());

        if (!dates.length) { return { minDate: null, maxDate: null }; }

        return {
            minDate: dates[0],
            maxDate: dates[dates.length - 1]
        };
    }, [outlierData]);

    useEffect(() => {
        if (!dateBounds.minDate || !dateBounds.maxDate) return;

        const nextFrom = formatInputDate(dateBounds.minDate);
        const nextTo = formatInputDate(dateBounds.maxDate);

        setDateRange((prev) => {
            if (prev.from === nextFrom && prev.to === nextTo && prev.quickRange === 'ALL') { return prev; }
            return { from: nextFrom, to: nextTo, quickRange: 'ALL' };
        });
    }, [dateBounds]);

    const disabledQuickOptions = useMemo(() => {
        const { minDate, maxDate } = dateBounds;

        if (!minDate || !maxDate) {
            return {
                ALL: true,
                '1Y': true,
                '1M': true,
                '1W': true,
                '1D': true
            };
        }

        const minTime = minDate.getTime();

        return {
            ALL: false,
            '1Y': subYears(maxDate, 1).getTime() < minTime,
            '1M': subMonths(maxDate, 1).getTime() < minTime,
            '1W': subWeeks(maxDate, 1).getTime() < minTime,
            '1D': subDays(maxDate, 1).getTime() < minTime
        };
    }, [dateBounds]);

    const filteredRows = useMemo(() => {
        let rows = outlierData;

        const fromDate = safeDate(dateRange?.from);
        const toDate = safeDate(dateRange?.to);

        if (fromDate || toDate) {
            const fromTime = fromDate ? fromDate.getTime() : -Infinity;
            const toTime = toDate ? endOfDay(toDate).getTime() : Infinity;

            rows = rows.filter((row) => {
                const date = safeDate(row?.ts);
                if (!date) return false;

                const time = date.getTime();
                return time >= fromTime && time <= toTime;
            });
        }

        if (outlierView === 'has') {
            rows = rows.filter((row) => row.outlier === true || row.outlier === false);
        } else if (outlierView === 'yes') {
            rows = rows.filter((row) => row.outlier === true);
        } else if (outlierView === 'no') {
            rows = rows.filter((row) => row.outlier === false);
        }

        return rows;
    }, [outlierData, outlierView, dateRange]);

    const tableConfig = useMemo(() => {
        const baseColumns = [
            {
                key: 'ts',
                label: 'Timestamp',
                exportLabel: 'Timestamp',
                sortable: true,
                defaultSortDirection: 'desc',
                width: 140,
                minWidth: 140,
                align: 'left',
                getSortValue: (row) => row?.ts,
                exportValue: (row) => formatDate(row?.ts),
                renderCell: ({ row }) => formatDate(row?.ts)
            },
            {
                key: 'outlier',
                label: 'Outlier',
                exportLabel: 'Outlier',
                sortable: true,
                width: 120,
                minWidth: 120,
                align: 'center',
                getSortValue: (row) => {
                    if (row?.outlier === true) { return 1; }
                    if (row?.outlier === false) { return 0; }
                    return -1;
                },
                exportValue: (row) => formatOutlierValue(row?.outlier),
                renderCell: ({ row }) => renderOutlierBadge(row?.outlier)
            }
        ];

        const dynamicColumns = detectorNames.map((name) => {
            return {
                key: name,
                exportLabel: name,
                label: (
                    <div className='flex flex-col leading-tight overflow-hidden' style={{ maxWidth: 160 }}>
                        <Tooltip title={name} arrow><span className='font-semibold truncate'>{name}</span></Tooltip>
                    </div>
                ),
                sortable: true,
                minWidth: 160,
                align: 'center',
                getSortValue: (row) => {
                    const prediction = row?.detectors?.[name]?.prediction;
                    if (prediction === true) { return 1; }
                    if (prediction === false) { return 0; }
                    return -1;
                },
                exportValue: (row) => formatPredictionValue(row?.detectors?.[name]?.prediction),
                renderCell: ({ row }) => renderDetectorCell(row, name)
            };
        });

        return {
            columns: [...baseColumns, ...dynamicColumns],
            sort: INITIAL_SORT,
            states: {
                loadingTitle: 'Loading data validation history...',
                errorTitle: 'Failed to load data validation history',
                emptyTitle: 'No data validation history available',
                noResultsTitle: 'No matching data validation rows'
            }
        };
    }, [detectorNames]);

    const exportDisabled = mergedMeasurementDataListLoading || !!mergedMeasurementDataListError || !filteredRows.length;

    const handleExport = (formatType) => {
        exportPopupTable({
            tableConfig,
            rows: filteredRows,
            sort: tableSort,
            filename: buildExportFilename('data-validation-history', ts),
            format: formatType
        });
    };

    return (
        <OptimizationPipelinePopup open={!!popup} onClose={closePopup} width={popup?.width || '1440px'} minWidth={popup?.minWidth || 480} maxWidth={popup?.maxWidth || 1440} className={popup?.className}>

            <OptimizationPipelinePopupHeader title='Data Validation History' subtitle={getDateRangeSubtitle(dateRange)} icon={PiClockCounterClockwiseDuotone} onClose={closePopup}>
                <div className='flex items-center gap-8'>
                    <PopupDateRangeQuickSelector value={dateRange} onChange={setDateRange} minDate={dateBounds.minDate} maxDate={dateBounds.maxDate} disabledQuickOptions={disabledQuickOptions} />
                    <PopupExportButtons disabled={exportDisabled} onExcel={() => handleExport('excel')} onCsv={() => handleExport('csv')} />
                </div>
            </OptimizationPipelinePopupHeader>

            {outlierData.length > 0 && (
                <PopupToolbar>
                    <PopupToolbarGroup label='Outlier:'>
                        <PopupToolbarChip label='All' active={outlierView === 'all'} onClick={() => { setOutlierView('all'); }} />
                        <PopupToolbarChip label='Has data' active={outlierView === 'has'} onClick={() => { setOutlierView('has'); }} />
                        <PopupToolbarChip label='Yes' active={outlierView === 'yes'} onClick={() => { setOutlierView('yes'); }} />
                        <PopupToolbarChip label='No' active={outlierView === 'no'} onClick={() => { setOutlierView('no'); }} />
                    </PopupToolbarGroup>
                </PopupToolbar>
            )}

            <OptimizationPipelinePopupTable
                tableConfig={tableConfig}
                rows={filteredRows}
                loading={mergedMeasurementDataListLoading}
                error={mergedMeasurementDataListError}
                minHeight={360}
                sort={tableSort}
                onSortChange={setTableSort}
                rowKey={(row, index) => row?.ts || index}
            />

        </OptimizationPipelinePopup>
    );
}